import { gql } from '@apollo/client'
import format from 'date-fns/format'
import parseISO from 'date-fns/parseISO'
import React, { useContext } from 'react'
import Link from 'next/link'
import OrdersProgress, {
  orderProgressFragment
} from 'components/pages/ShiftPage/OrdersProgress'
import stripTimeZone from 'utils/stripTimeZone'
import { ShiftPageContext } from 'components/pages/ShiftPage/ShiftPageProvider'
import cn from 'classnames'
import { ClockIcon, LocationMarkerIcon } from '@heroicons/react/outline'

import { locationInformationFragment as locationInformationFragmentType } from './types/locationInformationFragment'

export const locationInformationFragment = gql`
  ${orderProgressFragment}
  fragment locationInformationFragment on Location {
    id
    address
    allowOrders
    startTime
    endTime
    workStatus
    workStatusHuman
    lot {
      id
      name
      address
      fullAddress
      appNotice
      lotUrl
    }
    ...orderProgressFragment
  }
`
type Props = {
  location: locationInformationFragmentType
  hideOrdersProgress?: boolean
}

const Row = ({
  icon,
  content
}: {
  icon: React.ReactElement
  content: React.ReactElement
}) => (
  <div className="w-full flex flex-row items-start mr-6">
    <div className="lg:m-0 -mt-1">{icon}</div>
    <div className="mx-2 mb-4">{content}</div>
  </div>
)

const LocationInformation = ({
  location,
  hideOrdersProgress = false
}: Props) => {
  const { albertsons } = useContext(ShiftPageContext)
  return (
    <div className="mb-2 text-gray-50 lg:mt-6 mt-4">
      <div className="flex flex-col">
        <div className="flex mt-2 flex-col md:flex-row">
          <div className="lg:w-1/2">
            <Row
              content={
                <>
                  <p className="font-medium text-sm sm:text-base underline cursor-pointer">
                    <Link href={location.lot.lotUrl!} prefetch={false}>
                      {location.lot.name}
                    </Link>
                  </p>
                  <p className="break-words text-sm underline">
                    <a
                      aria-describedby="new-window-link"
                      href={`https://www.google.com/maps/search/${encodeURIComponent(
                        location.lot.fullAddress!
                      )}`}
                      rel="noreferrer"
                      target="_blank"
                    >
                      {location.lot.address}
                    </a>
                    <span className="hidden" id="new-window-link">
                      Opens in a new window
                    </span>
                  </p>
                </>
              }
              icon={
                <LocationMarkerIcon
                  className={cn('sm:h-8 h-6 sm:w-8 w-6 mt-1 sm:mt-0', {
                    'text-orange-600': !albertsons,
                    'text-albertsons-neutral5': albertsons
                  })}
                />
              }
            />

            <Row
              content={
                <p className="text-sm font-medium sm:text-base">
                  {` 
          ${format(parseISO(stripTimeZone(location.startTime)), 'h:mm aaaa')} -
          ${format(
            parseISO(stripTimeZone(location.endTime)),
            'h:mm aaaa'
          )}, ${format(parseISO(stripTimeZone(location.startTime)), 'M/d/y')}`}
                </p>
              }
              icon={
                <ClockIcon
                  className={cn('sm:h-8 h-6 sm:w-8 w-6 mt-1 sm:mt-0', {
                    'text-orange-600': !albertsons,
                    'text-albertsons-neutral5': albertsons
                  })}
                />
              }
            />
          </div>

          <div className="md:w-1/2 hidden lg:block">
            {location.showAmountLeftToOrder && !hideOrdersProgress && (
              <OrdersProgress
                id="truck-hero-section"
                location={location}
                textColor="white"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default LocationInformation
